import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  SelectChangeEvent,
  useTheme,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AxiosResponse, GetObservationTaskListApi, ProjectListingApi, TaskListingApi } from "../utils/urlConst";
import apiService from "../apiService/ApiService";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import CommonLoader from "../components/CommonLoader";
import { useAlert } from "../contexts/AlertContext";
import { useProjectContext } from "../contexts/ProjectContext";
import { useTranslation } from "react-i18next";
import useResponsive from "../hooks/useResponsive";
import HulpmiddelSection from "../components/TaskOverview/HulpmiddelSection";
import ProjectDropdown from "../components/TaskOverview/ProjectDropdown";
import SearchInputBox from "../components/TaskOverview/SearchInputBox";
import TaskTable from "../components/TaskOverview/TaskTable";
import MobileTaskCard from "../components/TaskOverview/MobileTaskCard";
import { Task, Project, Tool } from '../interfaces/TaskOverview'
import { useTimerContext } from "../contexts/TimerContext";
import SortAsc from '../assets/sort_asc.svg?react';
import SortDesc from '../assets/sort_desc.svg?react';

export interface Category {
  id: number | null;
  project_name: string;
}

const TaskOverview: React.FC = () => {

  const [searchTerm, setSearchTerm] = useState<string>("");
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile, isTablet, isExceedsHulp } = useResponsive();

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [taskList, setTaskList] = useState<Task[]>([])
  const [tool, setTool] = useState<Tool[]>([]);
  const [layerData, setLayerData] = useState<any>({});
  const [direction, setDirection] = useState<"asc" | "desc">("asc");
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [filterTaskList, setFilterTaskList] = useState<Task[]>(taskList);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const { showAlert } = useAlert();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const userName = LocalStorageUtil.getItem(storageKeys.userName, "");

  const tableCardMobileStyle = {
    fontSize: '14px',
    fontFamily: 'Nunito Sans Regular500',
    lineHeight: '19.1px',
    color: theme.palette.blueColors.main,
  };

  const {
    setSelectedProjectId,
    setSelectedProjectName,
    setSelectedProjectItem,
    selectedProjectId,
    selectedProjectName,
    clearProject
  } = useProjectContext();

  const { setSelectedProjectIdForNavigation } = useTimerContext()

  useEffect(() => {
    // projectList fetch api call
    getProjectList(selectedProjectId, selectedProjectName);
  }, []);

  // fetch ProjectList api call
  const getProjectList = async (selectedProjectId: number | string | null, selectedProjectName: string | null) => {
    try {
      setIsLoading(true)
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let isFlexRole = LocalStorageUtil.getItem(storageKeys.isFlexRole, 0);
      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        isFlexRole: isFlexRole,
      });

      apiService.setAuthToken(userToken);
      const responseJson: AxiosResponse | null = await apiService.post(ProjectListingApi, jsondata);
      if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
        if (responseJson?.data?.project_listing?.length) {
          let categoryList = responseJson?.data?.project_listing.map((category: Category) => {
            return {
              ...category,
            };
          });
          categoryList.sort((firstCategory: Category, secondCategory: Category) => firstCategory?.project_name.localeCompare(secondCategory?.project_name));
          let defaultItem = { id: null, project_name: t("common.All") };
          let newCategoryListAsc = [defaultItem].concat(categoryList);
          setProjectList(newCategoryListAsc);
          let projectId = selectedProjectId
          let projectName = selectedProjectName
          const sortedProjectList = newCategoryListAsc?.filter(item => {
            return projectName ? item?.project_name.includes(projectName) : true;
          });

          if (sortedProjectList?.length && projectName && projectName !== t("common.All")) {
            setSelectedProjectItem({ id: projectId, name: projectName })
            setSelectedProjectId(projectId ? Number(projectId) : null)
            await getObsTaskList(projectId, 1);
          } else {
            setSelectedProjectItem({ id: null, name: t("common.All") })
            await getObsTaskList(null, 1);
            setSelectedProjectId(null)
          }
        } else {
          await getObsTaskList(null, 1);
          setSelectedProjectId(null)
          setProjectList([]);
        }
        setCurrentPage(1)
      } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
        clearProject()
        LocalStorageUtil.clear();
        navigate("/login");
        setIsLoading(false);
      } else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
        setProjectList([]);
        setSelectedProjectItem({ id: null, name: null })
        setFilterTaskList([]);
        setTaskList([]);
        showAlert(t("taskOverView.no_projects_associated_with_worker"), "info")
        setIsLoading(false);
      } else {
        showAlert(t("common.server_error"), 'error')
        setIsLoading(false);
      }
    } catch (error) {
      showAlert(t("common.server_error"), 'error')
    }
  };

  // Ftech Next Page 
  const getNextTaskList = async () => {
    const nextPage = currentPage + 1
    getObsTaskList(selectedProjectId, nextPage)
  }

  // fetch TaskList api call
  const getObsTaskList = async (proId: number | string | null, page: number, search_query = searchTerm, sort = direction, sort_field = selectedColumn) => {
    try {
      // For pagination (page > 1), not for initial loads
      if (page > 1) {
        setIsFetchingNextPage(true);
      }
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let isFlexRole = LocalStorageUtil.getItem(storageKeys.isFlexRole, 0);
      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        project_id: proId,
        search_query: search_query,
        sort: sort,
        sort_field: sort_field,
        page: page,
        isFlexRole: isFlexRole,
      });
      apiService.setAuthToken(userToken);
      const responseJson: AxiosResponse | null = await apiService.get(GetObservationTaskListApi, jsondata);
      if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
        setTool(responseJson?.data?.hulpmiddelen ?? null);
        setLayerData(responseJson?.data?.layer_data ?? {});
        if (responseJson?.data.tasks_listing.data.length) {
          let taskListFilter = responseJson?.data?.tasks_listing.data
          if (taskListFilter) {
            setTaskList(page === 1 ? taskListFilter : [...taskList, ...taskListFilter]);
            setFilterTaskList(page === 1 ? taskListFilter : [...filterTaskList, ...taskListFilter]);
          }
          setHasNextPage(page < responseJson?.data.tasks_listing.last_page)
        }
      } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
        LocalStorageUtil.clear();
        clearProject()
        navigate('/login');
      }
      // When logged in user has no associated tasks
      else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
        // setSelectedProjectItem({ id: null, name: t("common.All") })
        setFilterTaskList([]);
      } else {
        showAlert(t("common.server_error"), 'error')
      }
    } catch (error) {
      showAlert(t("common.server_error"), 'error')
    } finally {
      setIsLoading(false);
      setIsFetchingNextPage(false)
      setCurrentPage(page)
    }
  };

  // refresh projectList
  const getRefreshProjectList = async () => {
    setSelectedProjectItem({ id: null, name: t("common.All") })
    setSelectedProjectId(null)
    setSelectedProjectName(t("common.All"))
    setSearchTerm('')
    await getProjectList(null, t("common.All"))
  }

  // to refresh the dropdown list 
  const dropDownPress = () => {
    if (projectList === null) {
      getProjectList(null, t("common.All"));
    }
  };

  // dropdown selection
  const handleProjectChange = async (event: SelectChangeEvent<string>) => {
    const selectedProject = JSON.parse(event.target.value);
    setSelectedProjectId(selectedProject?.id)
    setSelectedProjectName(selectedProject?.name)
    setSelectedProjectItem(selectedProject)
    setIsLoading(true)
    //Fetching from API - getProjectList calls getObsTaskList
    await getProjectList(selectedProject?.id, selectedProject?.name);
    setCurrentPage(1);
  };

  // search function
  const handleSearchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    setSearchTerm(text);
    await getObsTaskList(selectedProjectId, 1, text)
    setCurrentPage(1);
  };

  // sort function
  const sortTable = async (column: string) => {
    const newDirection = selectedColumn !== column ? "desc" : direction === "asc" ? "desc" : "asc";
    setSelectedColumn(column);
    setDirection(newDirection);
    setIsLoading(true);
    await getObsTaskList(selectedProjectId, 1, searchTerm, newDirection, column)
    setCurrentPage(1);
  };

  const getSortIcon = () => {
    return direction === 'asc'
      ? <SortAsc style={{ width: 20, height: 18, color: theme.palette.primary.main }} />
      : <SortDesc style={{ width: 20, height: 18, color: theme.palette.primary.main }} />;
  };

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && hasNextPage) {
      getNextTaskList();
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    });
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [handleObserver]);
  useEffect(() => {
    setSelectedProjectIdForNavigation(null)
  }, [])

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {
        !isMobile ?
          (<Box
            component="main"
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              width: '100%',
            }}
          >
            <CommonLoader isVisible={isLoading} />
            <Box sx={{
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  borderRadius: 1,
                  p: 3,
                  flexDirection: isTablet ? "column" : "row",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    width: isTablet ? "80%" : "50%"
                  }}
                >
                  <Box sx={{ mr: 1 }}>
                    <Typography
                      fontFamily={isTablet ? "Nunito Sans Regular700" : "Nunito Sans SemiBold"}
                      fontWeight={isTablet ? '700px' : '600px'}
                      fontSize={'22px'}
                      lineHeight={isTablet ? '32.74px' : '38.19px'}
                      textAlign={'center'}
                      sx={{
                        color: theme.palette.blueColors.main
                      }}>
                      Projectnaam
                    </Typography>
                  </Box>
                  <ProjectDropdown
                    projectList={projectList}
                    handleProjectChange={handleProjectChange}
                    dropDownPress={dropDownPress}
                    getRefreshProjectList={getRefreshProjectList}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    width: "100%",
                    mt: isExceedsHulp ? 2 : null,
                    justifyContent: isTablet ? 'flex-end' : 'center',
                    alignItems: 'center',
                  }}
                >
                  {selectedProjectId && tool?.length > 0 && (
                    <HulpmiddelSection tool={tool} />
                  )}
                </Box>
              </Box>

              <Box sx={{
                pb: 3
              }}>
                <Divider
                  sx={{
                    height: '1px',
                    backgroundColor: theme.palette.primary.light
                  }}
                />
              </Box>

              <Box sx={{
                mb: 2,
                pl: 3,
                pr: 3
              }}>
                <SearchInputBox searchTerm={searchTerm} handleSearchChange={handleSearchChange} />
              </Box>

              <Box sx={{
                pl: 3,
                pr: 3,
                mb: 6,
                width: '100%',
              }}>
                <TaskTable
                  filterTaskList={filterTaskList}
                  sortTable={sortTable}
                  getSortIcon={getSortIcon}
                  fetchNextPage={getNextTaskList}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  layerData={layerData}
                />
              </Box>

            </Box>
          </Box>)
          :
          (<Box
            sx={{
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
              maxWidth: '100vw'
            }}
          >
            <CommonLoader isVisible={isLoading} />
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  margin: 2,
                  maxWidth: '100vw',
                  overflow: 'hidden',
                }}
              >
                <Typography sx={{
                  fontFamily: 'Nunito Sans Regular700',
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: '21.82px',
                  color: theme.palette.blueColors.main
                }}>
                  Projectnaam
                </Typography>

                <ProjectDropdown
                  projectList={projectList}
                  handleProjectChange={handleProjectChange}
                  dropDownPress={dropDownPress}
                  getRefreshProjectList={getRefreshProjectList}
                />
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                maxWidth: '100vw',
              }}>
                {selectedProjectId && tool?.length > 0 && (
                  <HulpmiddelSection tool={tool} />
                )}
              </Box>
            </Box>

            <Divider sx={{ backgroundColor: theme.palette.success.light, height: '1px', mb: 3 }} />

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 2
            }}>
              <SearchInputBox searchTerm={searchTerm} handleSearchChange={handleSearchChange} />
            </Box>

            <Box sx={{ mb: 10 }}>
              {filterTaskList?.length > 0 ? (
                filterTaskList.map((task, index) => (
                  <MobileTaskCard
                    key={index}
                    task={task}
                    layerData={layerData}
                  />
                ))
              ) : (
                <Typography
                  sx={{
                    ...tableCardMobileStyle,
                    textAlign: 'center',
                    marginTop: 5
                  }}>
                  {t('taskOverViewScreen.noPendingTasks')}
                </Typography>
              )}
            </Box>
            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pb: 5 }}>
                <CircularProgress />
              </Box>
            )}
            <div ref={loadMoreRef} style={{ height: 1 }} />
          </Box>)
      }
    </Box >
  );
};

export default TaskOverview;